/**
 * Can we reach the API? One app-wide answer, held outside React so the axios
 * interceptors can read and feed it as well as the UI.
 *
 *   online      — the API is answering.
 *   offline     — the browser has no network at all (its `offline` event).
 *   unreachable — the browser has a network, but the API isn't answering.
 *
 * `navigator.onLine` alone isn't enough: `false` is reliable, but `true` only
 * means an adapter is up — a nursery Wi-Fi with no internet behind it still
 * reports online. So the second signal is a request that failed with no
 * response at all, confirmed by a probe of the API's unauthenticated root
 * route before anything is shown. Recovery is the same probe succeeding.
 *
 * The probe is `no-cors`: any answer, even an opaque one, proves the server is
 * there, and only a genuine network failure rejects. It also means a 5xx that
 * lost its CORS headers (which axios sees as "no response") is correctly not
 * mistaken for an outage.
 */

export type ConnectivityStatus = 'online' | 'offline' | 'unreachable';

export type ConnectivitySnapshot = {
  status: ConnectivityStatus;
  /** Date.now() of the last return to `online` from a lost state; 0 if never. */
  recoveredAt: number;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
// The API's root `GET /` sits outside `/api` and needs no auth.
const PROBE_URL = API_BASE_URL.replace(/\/api\/?$/, '') + '/';
const PROBE_TIMEOUT_MS = 8_000;
// Backoff between recovery probes while lost.
const PROBE_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000];

let snapshot: ConnectivitySnapshot = {
  status: typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online',
  recoveredAt: 0,
};
const listeners = new Set<() => void>();

let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probeAttempt = 0;
let inFlightProbe: Promise<boolean> | null = null;

function setStatus(status: ConnectivityStatus) {
  if (snapshot.status === status) return;
  const recovered = status === 'online' && snapshot.status !== 'online';
  snapshot = { status, recoveredAt: recovered ? Date.now() : snapshot.recoveredAt };
  listeners.forEach(l => l());
}

export function getConnectivity(): ConnectivitySnapshot {
  return snapshot;
}

export function isOnline(): boolean {
  return snapshot.status === 'online';
}

export function subscribeConnectivity(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** One probe at a time; concurrent callers share it. Resolves true if the API answered. */
function probe(): Promise<boolean> {
  if (inFlightProbe) return inFlightProbe;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  inFlightProbe = fetch(PROBE_URL, { mode: 'no-cors', cache: 'no-store', signal: controller.signal })
    .then(() => true, () => false)
    .finally(() => {
      clearTimeout(timeout);
      inFlightProbe = null;
    });
  return inFlightProbe;
}

function stopProbing() {
  if (probeTimer) clearTimeout(probeTimer);
  probeTimer = null;
  probeAttempt = 0;
}

function scheduleProbe() {
  if (probeTimer) return;
  const delay = PROBE_DELAYS_MS[Math.min(probeAttempt, PROBE_DELAYS_MS.length - 1)];
  probeAttempt += 1;
  probeTimer = setTimeout(async () => {
    probeTimer = null;
    await checkNow();
  }, delay);
}

/**
 * Probe the API now and settle the status on the answer. Returns true when
 * online. Keeps a backoff loop running for as long as we're lost; the
 * browser's own `offline` state is trusted without probing.
 */
export async function checkNow(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    stopProbing();
    setStatus('offline');
    return false;
  }
  const ok = await probe();
  // The browser may have gone offline while the probe was out.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    stopProbing();
    setStatus('offline');
    return false;
  }
  if (ok) {
    stopProbing();
    setStatus('online');
    return true;
  }
  setStatus('unreachable');
  scheduleProbe();
  return false;
}

/**
 * A request got no response at all. Not proof by itself — the probe decides
 * whether it was the network or something else (an aborted page load, a
 * CORS-less error response).
 */
export function reportNetworkFailure(): void {
  if (snapshot.status === 'offline') return;
  void checkNow();
}

/**
 * True when a Cognito/Amplify call failed for want of a network rather than
 * because the session is dead. amazon-cognito-identity-js tags these
 * `code: 'NetworkError'`; a browser that reports itself offline counts too.
 * A token refresh failing this way must never be read as "session expired".
 */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const e = err as { code?: string; name?: string; message?: string } | null | undefined;
  return e?.code === 'NetworkError' || e?.name === 'NetworkError' || e?.message === 'Network error';
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    stopProbing();
    setStatus('offline');
  });
  // `online` only means an adapter came back — confirm the API before saying so.
  window.addEventListener('online', () => {
    stopProbing();
    void checkNow();
  });
}

/** Test-only: reset module state between tests. */
export function __resetConnectivityForTests(status: ConnectivityStatus = 'online') {
  stopProbing();
  inFlightProbe = null;
  snapshot = { status, recoveredAt: 0 };
}
